import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  createTable,
  createZone,
  deleteTable,
  deleteZone,
  discardException,
  fetchApprovalLog,
  fetchApprovalPolicy,
  fetchException,
  fetchExceptions,
  fetchFloor,
  fetchRegisters,
  fetchSessions,
  fetchTables,
  fetchXReport,
  fetchZReport,
  fetchZones,
  forceCloseSession,
  resolveException,
  retireRegister,
  updateApprovalPolicy,
  updateTable,
  updateZone,
} from '../api'
import type {
  ApprovalLogQuery,
  CreateTableBody,
  CreateZoneBody,
  ExceptionQuery,
  ExceptionResolution,
  RegisterQuery,
  SessionQuery,
  TableQuery,
  UpdateApprovalPolicyBody,
  UpdateTableBody,
  UpdateZoneBody,
} from '../types'

export const posKey = ['pos'] as const

/**
 * How often the live views re-ask the server.
 *
 * Fifteen seconds against a till that heartbeats every minute: quick enough
 * that a register going quiet shows up while somebody is still looking at the
 * screen, slow enough that a floor left open on a back-office monitor is not
 * four requests a minute per manager. The lists that are not live — sessions,
 * exceptions, the audit log — do not poll at all.
 */
const LIVE_REFETCH_MS = 15_000

/* -------------------------------------------------------------------------- */
/* Registers                                                                  */
/* -------------------------------------------------------------------------- */

export function useRegisters(query: RegisterQuery) {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...posKey, 'registers', query],
    queryFn: () => fetchRegisters(query),
    // `health` is computed server-side from `lastSeenAt`, so a stale cache
    // would show a dead till as Active. Polling is what makes the column true.
    refetchInterval: LIVE_REFETCH_MS,
    placeholderData: (previous) => previous,
  })

  return {
    registers: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

export function useRegisterActions() {
  const queryClient = useQueryClient()

  const retire = useMutation({
    mutationFn: (id: string) => retireRegister(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...posKey, 'registers'] }),
  })

  return { retire }
}

/* -------------------------------------------------------------------------- */
/* Floor                                                                      */
/* -------------------------------------------------------------------------- */

export function useFloor(branchIds: readonly string[]) {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...posKey, 'floor', branchIds],
    queryFn: () => fetchFloor(branchIds),
    // The one view that is genuinely live: a table's elapsed time is computed
    // by the API at read, so the grid only moves when it refetches.
    refetchInterval: LIVE_REFETCH_MS,
    placeholderData: (previous) => previous,
  })

  return {
    floors: data ?? [],
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

export function useTables(query: TableQuery) {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...posKey, 'tables', query],
    queryFn: () => fetchTables(query),
    placeholderData: (previous) => previous,
  })

  return {
    tables: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

export function useZones(branchIds: readonly string[]) {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...posKey, 'zones', branchIds],
    queryFn: () => fetchZones(branchIds),
    placeholderData: (previous) => previous,
  })

  return {
    zones: data ?? [],
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

/**
 * Every floor write invalidates the whole POS tree.
 *
 * Moving a table changes the grid, the setup list AND both zones' counts, and
 * a hook that invalidated only its own list would leave two of those three
 * showing yesterday.
 */
export function useFloorActions() {
  const queryClient = useQueryClient()

  function invalidate() {
    return queryClient.invalidateQueries({ queryKey: posKey })
  }

  const createTableMutation = useMutation({
    mutationFn: (body: CreateTableBody) => createTable(body),
    onSuccess: invalidate,
  })

  const updateTableMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateTableBody }) => updateTable(id, body),
    onSuccess: invalidate,
  })

  const removeTable = useMutation({
    mutationFn: (id: string) => deleteTable(id),
    onSuccess: invalidate,
  })

  const createZoneMutation = useMutation({
    mutationFn: (body: CreateZoneBody) => createZone(body),
    onSuccess: invalidate,
  })

  const updateZoneMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateZoneBody }) => updateZone(id, body),
    onSuccess: invalidate,
  })

  const removeZone = useMutation({
    mutationFn: (id: string) => deleteZone(id),
    onSuccess: invalidate,
  })

  return {
    createTable: createTableMutation,
    updateTable: updateTableMutation,
    removeTable,
    createZone: createZoneMutation,
    updateZone: updateZoneMutation,
    removeZone,
  }
}

/* -------------------------------------------------------------------------- */
/* Sessions                                                                   */
/* -------------------------------------------------------------------------- */

export function useSessions(query: SessionQuery) {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...posKey, 'sessions', query],
    queryFn: () => fetchSessions(query),
    placeholderData: (previous) => previous,
  })

  return {
    sessions: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

/**
 * The X report for one drawer. Only fetched once a drawer is actually chosen.
 *
 * Polls while that drawer is still trading, because that is the only state where
 * an X goes stale — the figures are re-derived at read, so a panel left open on a
 * live session would otherwise show the position as it was when the screen
 * opened. Once counted, the window is closed and there is nothing to poll for.
 *
 * Safe to poll at all only because an X is a pure read: it does not touch the
 * session, and taking a hundred of them is indistinguishable from taking none.
 */
export function useXReport(sessionId: string | null, isLive = false) {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...posKey, 'x-report', sessionId],
    queryFn: () => fetchXReport(sessionId as string),
    enabled: Boolean(sessionId),
    refetchInterval: isLive ? LIVE_REFETCH_MS : false,
  })

  return {
    xReport: data ?? null,
    isLoading: Boolean(sessionId) && isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

/**
 * The Z report for one drawer.
 *
 * Never polled, and `staleTime: Infinity` for the life of the cache: a Z is a
 * frozen document written inside the closing transaction, so asking twice is
 * asking the same question. The two states where it comes back empty — a drawer
 * still trading, a force-close nobody counted — are answers, not failures.
 */
export function useZReport(sessionId: string | null) {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: [...posKey, 'z-report', sessionId],
    queryFn: () => fetchZReport(sessionId as string),
    enabled: Boolean(sessionId),
    staleTime: Infinity,
  })

  return {
    zReport: data ?? null,
    isLoading: Boolean(sessionId) && isPending,
    error,
    retry: () => void refetch(),
  }
}

export function useSessionActions() {
  const queryClient = useQueryClient()

  const forceClose = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => forceCloseSession(id, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: posKey }),
  })

  return { forceClose }
}

/* -------------------------------------------------------------------------- */
/* Exceptions                                                                 */
/* -------------------------------------------------------------------------- */

export function useExceptions(query: ExceptionQuery) {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...posKey, 'exceptions', query],
    queryFn: () => fetchExceptions(query),
    placeholderData: (previous) => previous,
  })

  return {
    exceptions: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

/**
 * The count behind the tab's badge.
 *
 * Its own single-row query rather than a number read off the list: the badge
 * has to be right on the Registers tab, before the exceptions list has been
 * asked for at all.
 */
export function useOpenExceptionCount(branchIds: readonly string[]) {
  const { data } = useQuery({
    queryKey: [...posKey, 'exceptions', 'open-count', branchIds],
    queryFn: () =>
      fetchExceptions({
        limit: 1,
        offset: 0,
        status: 'open',
        // The badge counts the same outlets the queue below it lists. A badge
        // reading 4 over a filtered list showing 1 is read as three exceptions
        // that have gone missing.
        branchIds: branchIds.length ? [...branchIds] : undefined,
      }),
    refetchInterval: LIVE_REFETCH_MS,
    placeholderData: (previous) => previous,
  })

  return data?.count ?? 0
}

export function useException(id: string | null) {
  const { data, isPending, error } = useQuery({
    queryKey: [...posKey, 'exception', id],
    queryFn: () => fetchException(id as string),
    enabled: Boolean(id),
  })

  return { exception: data ?? null, isLoading: Boolean(id) && isPending, error }
}

export function useExceptionActions() {
  const queryClient = useQueryClient()

  function invalidate() {
    return queryClient.invalidateQueries({ queryKey: posKey })
  }

  const resolve = useMutation({
    mutationFn: ({
      id,
      resolution,
      note,
    }: {
      id: string
      resolution: ExceptionResolution
      note?: string
    }) => resolveException(id, resolution, note),
    onSuccess: invalidate,
  })

  const discard = useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => discardException(id, note),
    onSuccess: invalidate,
  })

  return { resolve, discard }
}

/* -------------------------------------------------------------------------- */
/* Approvals                                                                  */
/* -------------------------------------------------------------------------- */

export function useApprovalPolicy(branchId: string | null) {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...posKey, 'approval-policy', branchId],
    queryFn: () => fetchApprovalPolicy(branchId ?? undefined),
    placeholderData: (previous) => previous,
  })

  return {
    policy: data ?? null,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}

export function useApprovalPolicyActions() {
  const queryClient = useQueryClient()

  const save = useMutation({
    mutationFn: (body: UpdateApprovalPolicyBody) => updateApprovalPolicy(body),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [...posKey, 'approval-policy'] }),
  })

  return { save }
}

export function useApprovalLog(query: ApprovalLogQuery) {
  const { data, isPending, isFetching, error, refetch } = useQuery({
    queryKey: [...posKey, 'approval-log', query],
    queryFn: () => fetchApprovalLog(query),
    placeholderData: (previous) => previous,
  })

  return {
    requests: data?.rows ?? [],
    total: data?.count ?? 0,
    isLoading: isPending,
    isRefreshing: isFetching && !isPending,
    error,
    retry: () => void refetch(),
  }
}
